import {AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {NzBadgeModule} from 'ng-zorro-antd/badge';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {NzEmptyModule} from 'ng-zorro-antd/empty';
import {NzDropDownModule} from 'ng-zorro-antd/dropdown';
import {NzMessageService} from 'ng-zorro-antd/message';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {AppNotification, NotificationReadState, NotificationService} from '../services/notification.service';
import {AuthService} from '../services/auth.service';
import {isValidAudience, isValidVariant, resolveNotificationParams} from '../utils/notification-labels.utils';

@Component({
  selector: 'app-notification-center',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzBadgeModule, NzButtonModule, NzIconModule, NzSpinModule, NzEmptyModule, NzDropDownModule],
  templateUrl: './notification-center.component.html',
  styleUrl: './notification-center.component.less'
})
export class NotificationCenterComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly readStates: NotificationReadState[] = ['all', 'unread', 'read'];
  unreadCount = 0;
  visible = false;
  loading = false;
  error = false;
  items: AppNotification[] = [];
  nextCursor: string | null = null;
  now = Date.now();
  readState: NotificationReadState = 'all';
  private readonly expandedNotificationIds = new Set<string>();
  private readonly overflowingNotificationIds = new Set<string>();
  private readonly pendingCollapsedOverflowMeasurementIds = new Set<string>();
  private readonly teamAccessPermissions = ['company.users.read', 'company.users.invite', 'company.users.update', 'company.users.remove'];
  private readonly propertyReadPermission = 'company.properties.read';
  private readonly signOrderNotificationStatuses = new Set(['payment_received', 'processing', 'shipped', 'completed', 'cancelled']);
  private unreadSubscription?: Subscription;
  private langChangeSubscription?: Subscription;
  private bodyElementsSubscription?: Subscription;
  private overflowMeasurementFrame: number | null = null;
  private panelResizeObserver?: ResizeObserver;
  private panelElementRef?: ElementRef<HTMLElement>;
  private observedPanelWidth: number | null = null;

  @ViewChildren('bodyEl') bodyElements!: QueryList<ElementRef<HTMLElement>>;

  @ViewChild('notificationPanel', { static: false })
  set notificationPanelElement(el: ElementRef<HTMLElement> | undefined) {
    this.panelElementRef = el;
    if (!el) {
      this.disconnectPanelResizeObserver();
      return;
    }
    if (this.visible) {
      this.attachPanelResizeObserver();
    }
  }

  constructor(private notifications: NotificationService, private router: Router, private message: NzMessageService, private translate: TranslateService, private auth: AuthService) {
  }

  ngOnInit(): void {
    this.notifications.start();
    this.unreadSubscription = this.notifications.unreadCount$.subscribe((count) => this.unreadCount = count);
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.overflowingNotificationIds.clear();
      for (const id of this.expandedNotificationIds) {
        this.pendingCollapsedOverflowMeasurementIds.add(id);
      }
      this.scheduleOverflowMeasurement();
    });
  }

  ngAfterViewInit(): void {
    this.bodyElementsSubscription = this.bodyElements.changes.subscribe(() => {
      this.scheduleOverflowMeasurement();
    });
  }

  ngOnDestroy(): void {
    this.cancelPendingOverflowMeasurement();
    this.disconnectPanelResizeObserver();
    this.unreadSubscription?.unsubscribe();
    this.langChangeSubscription?.unsubscribe();
    this.bodyElementsSubscription?.unsubscribe();
  }

  @HostListener('window:focus') onFocus(): void {
    this.notifications.refreshUnreadCount();
  }

  @HostListener('window:resize') onResize(): void {
    this.scheduleOverflowMeasurement();
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    if (visible) {
      this.expandedNotificationIds.clear();
      this.attachPanelResizeObserver();
      this.load();
    } else {
      this.expandedNotificationIds.clear();
      this.cancelPendingOverflowMeasurement();
      this.disconnectPanelResizeObserver();
    }
  }

  close(): void {
    this.visible = false;
  }

  load(cursor?: string): void {
    this.now = Date.now();
    this.loading = true;
    this.error = false;
    this.notifications.list(this.readState, cursor).subscribe({
      next: (page) => {
        this.items = cursor ? [...this.items, ...page.items] : page.items;
        this.nextCursor = page.nextCursor;
        this.loading = false;
        this.cleanupStaleIds();
        for (const id of this.expandedNotificationIds) {
          this.pendingCollapsedOverflowMeasurementIds.add(id);
        }
        this.scheduleOverflowMeasurement();
      }, error: () => {
        this.loading = false;
        this.error = true;
      }
    });
  }

  selectReadState(readState: NotificationReadState): void {
    if (this.readState !== readState) {
      this.readState = readState;
      this.load();
    }
  }

  markAllRead(): void {
    this.notifications.markAllRead().subscribe({
      next: () => {
        this.items = this.items.map((item) => ({...item, readAt: item.readAt || new Date().toISOString()}));
        this.notifications.refreshUnreadCount();
      }
    });
  }

  open(item: AppNotification): void {
    const navigate = () => {
      const target = this.targetNavigation(item);
      if (!target) return this.showTargetUnavailable();
      if (this.isTeamAccessUnavailable(item)) return this.showTargetAccessDenied();
      if (this.isPropertiesUnavailable(item)) return this.showPropertiesAccessDenied();
      if (this.isCurrentTarget(target)) return this.showCurrentTarget();
      this.router.navigate(target.commands, target.queryParams ? { queryParams: target.queryParams } : {})
        .then((ok) => ok ? this.close() : this.showTargetUnavailable());
    };
    if (item.type === 'platform_admin_custom_notification' && !item.target?.kind) {
      if (!item.readAt) this.notifications.markRead(item.id).subscribe({
        next: () => {
          item.readAt = new Date().toISOString();
          this.notifications.refreshUnreadCount();
        }
      });
      return;
    }
    if (item.readAt) return navigate();
    this.notifications.markRead(item.id).subscribe({
      next: () => {
        item.readAt = new Date().toISOString();
        this.notifications.refreshUnreadCount();
        navigate();
      }, error: () => this.showTargetUnavailable()
    });
  }

  icon(item: AppNotification): string {
    return ({
      success: 'check-circle',
      warning: 'warning',
      error: 'close-circle',
      info: 'info-circle'
    } as Record<string, string>)[item.severity] || 'info-circle';
  }

  title(item: AppNotification): string {
    if (item.type !== 'platform_admin_custom_notification') {
      if (item.type === 'sign_order_status_changed') {
        const status = item.params?.['status'] || '';
        if (!this.signOrderNotificationStatuses.has(status)) return '';
        const key = `NOTIFICATIONS.TYPES.${item.type}.STATUSES.${status}.TITLE`;
        const value = this.translate.instant(key, item.params);
        return value === key ? '' : value;
      }
      return this.translate.instant(`NOTIFICATIONS.TYPES.${item.type}.TITLE`, item.params);
    }
    const enRaw = item.content?.title?.en || '';
    const deRaw = item.content?.title?.de || '';
    const hasEn = enRaw.trim().length > 0;
    const hasDe = deRaw.trim().length > 0;
    if (this.translate.currentLang === 'de') return hasDe ? deRaw : (hasEn ? enRaw : '');
    return hasEn ? enRaw : (hasDe ? deRaw : '');
  }

  body(item: AppNotification): string {
    if (item.type === 'platform_admin_custom_notification') {
      const enRaw = item.content?.body?.en || '';
      const deRaw = item.content?.body?.de || '';
      const hasEn = enRaw.trim().length > 0;
      const hasDe = deRaw.trim().length > 0;
      if (this.translate.currentLang === 'de') return hasDe ? deRaw : (hasEn ? enRaw : '');
      return hasEn ? enRaw : (hasDe ? deRaw : '');
    }

    const resolvedParams = resolveNotificationParams(item.params, this.translate);
    const audience = isValidAudience(resolvedParams['audience']) ? resolvedParams['audience'] : null;
    const variant = isValidVariant(resolvedParams['variant']) ? resolvedParams['variant'] : null;
    const hasMemberDisplayName = !!(item.params?.['memberDisplayName']?.trim());
    const hasSubjectDisplayName = !!(
      resolvedParams['subjectIdentity'] && resolvedParams['subjectIdentity'] !==
      this.translate.instant('NOTIFICATIONS.IDENTITY.TEAM_MEMBER')
    );
    const canUseAudienceMessage = !!audience && this.canUseAudienceMessage(item, resolvedParams, audience, variant);

    const keyCandidates: string[] = [];

    this.pushContextualCandidates(item, resolvedParams, keyCandidates);

    if (audience && canUseAudienceMessage && variant) {
      keyCandidates.push(`NOTIFICATIONS.TYPES.${item.type}.MESSAGE_${audience.toUpperCase()}_${variant.toUpperCase()}`);
    }
    if (variant) {
      keyCandidates.push(`NOTIFICATIONS.TYPES.${item.type}.MESSAGE_VARIANT_${variant.toUpperCase()}`);
    }
    if (audience && canUseAudienceMessage) {
      keyCandidates.push(`NOTIFICATIONS.TYPES.${item.type}.MESSAGE_${audience.toUpperCase()}`);
    }

    const isTeamDeactivateOrRemove = item.type === 'team_member_deactivated' || item.type === 'team_member_removed';
    const needsGenericFallback = isTeamDeactivateOrRemove && !hasMemberDisplayName && !hasSubjectDisplayName;

    if (needsGenericFallback) {
      if (audience) {
        keyCandidates.push(`NOTIFICATIONS.TYPES.${item.type}.MESSAGE_GENERIC_${audience.toUpperCase()}`);
      }
      keyCandidates.push(`NOTIFICATIONS.TYPES.${item.type}.MESSAGE_GENERIC`);
    }

    if (isTeamDeactivateOrRemove && hasSubjectDisplayName && !hasMemberDisplayName) {
      this.pushActorlessCandidates(item, resolvedParams, audience, keyCandidates);
    }

    if (item.type === 'team_access_changed' && variant && audience) {
      this.pushAccessActorlessCandidates(resolvedParams, audience, variant, keyCandidates);
    }

    keyCandidates.push(`NOTIFICATIONS.TYPES.${item.type}.MESSAGE`);

    for (const key of keyCandidates) {
      const message = this.translate.instant(key, resolvedParams);
      if (this.isUsableResolvedMessage(key, message)) return message;
    }

    return '';
  }

  private hasValues(params: Record<string, string>, ...keys: string[]): boolean {
    return keys.every((k) => typeof params[k] === 'string' && params[k].trim().length > 0);
  }

  private canUseAudienceMessage(
    item: AppNotification,
    params: Record<string, string>,
    audience: string,
    variant: string | null,
  ): boolean {
    switch (item.type) {
      case 'property_import_completed':
        return audience === 'requester'
          ? this.hasValues(params, 'fileName', 'createdCount')
          : audience !== 'manager' || this.hasValues(params, 'actorIdentity', 'fileName', 'createdCount');
      case 'property_import_completed_with_errors':
        return audience === 'requester'
          ? this.hasValues(params, 'fileName', 'failedCount')
          : audience !== 'manager' || this.hasValues(params, 'actorIdentity', 'fileName', 'createdCount', 'failedCount');
      case 'property_import_failed':
        return audience === 'requester'
          ? this.hasValues(params, 'fileName')
          : audience !== 'manager' || this.hasValues(params, 'actorIdentity', 'fileName');
      case 'team_member_deactivated':
      case 'team_member_removed':
        return audience !== 'manager' || this.hasValues(params, 'subjectIdentity', 'actorIdentity');
      case 'team_user_inactive_60d':
        return audience !== 'manager' || this.hasValues(params, 'subjectIdentity', 'inactiveDays', 'subjectRole');
      case 'team_access_changed':
        if (variant === 'role') {
          return audience === 'manager'
            ? this.hasValues(params, 'subjectIdentity', 'previousRole', 'newRole', 'actorIdentity')
            : audience !== 'subject' || this.hasValues(params, 'previousRole', 'newRole', 'actorIdentity');
        }
        if (variant === 'permissions') {
          return audience === 'manager'
            ? this.hasValues(params, 'subjectIdentity', 'addedPermissionCount', 'removedPermissionCount')
            : audience !== 'subject' || this.hasValues(params, 'addedPermissionCount', 'removedPermissionCount');
        }
        if (variant === 'role_and_permissions') {
          return audience === 'manager'
            ? this.hasValues(params, 'subjectIdentity', 'previousRole', 'newRole', 'addedPermissionCount', 'removedPermissionCount')
            : audience !== 'subject' || this.hasValues(params, 'previousRole', 'newRole', 'addedPermissionCount', 'removedPermissionCount');
        }
        return false;
      default:
        return true;
    }
  }

  private isUsableResolvedMessage(key: string, message: unknown): message is string {
    if (typeof message !== 'string') return false;
    if (!message.trim()) return false;
    if (message === key) return false;
    if (/\{\{[^}]+\}\}/.test(message)) return false;
    return true;
  }

  private pushActorlessCandidates(
    item: AppNotification,
    p: Record<string, string>,
    audience: string | null,
    candidates: string[],
  ): void {
    const base = `NOTIFICATIONS.TYPES.${item.type}`;
    if (!this.hasValues(p, 'actorIdentity')) {
      if (audience) {
        candidates.push(`${base}.MESSAGE_${audience.toUpperCase()}_NO_ACTOR`);
      }
      candidates.push(`${base}.MESSAGE_NO_ACTOR`);
    }
  }

  private pushAccessActorlessCandidates(
    p: Record<string, string>,
    audience: string,
    variant: string,
    candidates: string[],
  ): void {
    if (this.hasValues(p, 'actorIdentity')) return;
    const base = `NOTIFICATIONS.TYPES.team_access_changed`;
    candidates.push(`${base}.MESSAGE_${audience.toUpperCase()}_${variant.toUpperCase()}_NO_ACTOR`);
    candidates.push(`${base}.MESSAGE_VARIANT_${variant.toUpperCase()}_NO_ACTOR`);
    candidates.push(`${base}.MESSAGE_${audience.toUpperCase()}_NO_ACTOR`);
  }

  private pushContextualCandidates(
    item: AppNotification,
    p: Record<string, string>,
    candidates: string[],
  ): void {
    const base = `NOTIFICATIONS.TYPES.${item.type}`;
    const aud = p['audience'];

    switch (item.type) {
      case 'property_import_completed':
        if (aud === 'requester') {
          if (this.hasValues(p, 'fileName', 'createdCount')) {
            candidates.push(`${base}.MESSAGE_REQUESTER`);
          }
        } else if (aud === 'manager') {
          if (this.hasValues(p, 'fileName', 'createdCount')) {
            if (this.hasValues(p, 'actorIdentity')) {
              candidates.push(`${base}.MESSAGE_MANAGER`);
            }
            candidates.push(`${base}.MESSAGE_MANAGER_FILE_ONLY`);
          }
        }
        break;

      case 'property_import_completed_with_errors':
        if (aud === 'requester') {
          if (this.hasValues(p, 'fileName', 'failedCount')) {
            candidates.push(`${base}.MESSAGE_REQUESTER`);
          }
        } else if (aud === 'manager') {
          if (this.hasValues(p, 'actorIdentity', 'fileName', 'createdCount', 'failedCount')) {
            candidates.push(`${base}.MESSAGE_MANAGER`);
          }
          if (this.hasValues(p, 'fileName', 'failedCount')) {
            candidates.push(`${base}.MESSAGE_MANAGER_FILE_ONLY`);
          }
        }
        break;

      case 'property_import_failed':
        if (aud === 'requester') {
          if (this.hasValues(p, 'fileName')) {
            candidates.push(`${base}.MESSAGE_REQUESTER`);
          }
        } else if (aud === 'manager') {
          if (this.hasValues(p, 'fileName')) {
            if (this.hasValues(p, 'actorIdentity')) {
              candidates.push(`${base}.MESSAGE_MANAGER`);
            }
            candidates.push(`${base}.MESSAGE_MANAGER_FILE_ONLY`);
          }
        }
        break;

      case 'integration_sync_partial':
        if (this.hasValues(p, 'provider', 'updatedCount', 'errorCount')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        } else if (this.hasValues(p, 'provider', 'errorCount')) {
          candidates.push(`${base}.MESSAGE_PROVIDER_AND_ERRORS`);
        } else if (this.hasValues(p, 'provider')) {
          candidates.push(`${base}.MESSAGE_PROVIDER_ONLY`);
        }
        break;

      case 'integration_sync_failed':
        if (this.hasValues(p, 'provider')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        }
        break;

      case 'billing_document_available':
        if (this.hasValues(p, 'documentType', 'documentNumber')) {
          if (this.hasValues(p, 'formattedAmount')) {
            candidates.push(`${base}.MESSAGE_ENRICHED`);
          } else {
            candidates.push(`${base}.MESSAGE_DOC_ONLY`);
          }
        } else if (this.hasValues(p, 'documentType', 'title')) {
          candidates.push(`${base}.MESSAGE_TITLE_ONLY`);
        }
        break;

      case 'billing_document_updated': {
        const ck = p['changeKind'];

        if (ck) {
          if (ck.startsWith('status_') && this.hasValues(p, 'documentIdentity', 'previousStatus', 'newStatus')) {
            candidates.push(`${base}.MESSAGE_STATUS_CHANGED`);
          } else if (ck.startsWith('amount_')) {
            if (ck === 'amount_changed' && this.hasValues(p, 'documentIdentity', 'formattedPreviousAmount', 'formattedNewAmount')) {
              candidates.push(`${base}.MESSAGE_AMOUNT_CHANGED`);
            } else if (ck === 'amount_set' && this.hasValues(p, 'documentIdentity', 'formattedNewAmount')) {
              candidates.push(`${base}.MESSAGE_AMOUNT_SET`);
            } else if (ck === 'amount_removed' && this.hasValues(p, 'documentIdentity', 'formattedPreviousAmount')) {
              candidates.push(`${base}.MESSAGE_AMOUNT_REMOVED`);
            }
          } else if (ck.startsWith('title_')) {
            if (ck === 'title_changed' && this.hasValues(p, 'documentType', 'previousTitle', 'newTitle')) {
              candidates.push(`${base}.MESSAGE_TITLE_CHANGED`);
            } else if (ck === 'title_set' && this.hasValues(p, 'documentType', 'newTitle')) {
              candidates.push(`${base}.MESSAGE_TITLE_SET`);
            } else if (ck === 'title_removed' && this.hasValues(p, 'documentType')) {
              candidates.push(`${base}.MESSAGE_TITLE_REMOVED`);
            }
          } else if (ck.startsWith('document_number_')) {
            if (ck === 'document_number_changed' && this.hasValues(p, 'documentType', 'previousDocumentNumber', 'newDocumentNumber')) {
              candidates.push(`${base}.MESSAGE_DOCUMENT_NUMBER_CHANGED`);
            } else if (ck === 'document_number_set' && this.hasValues(p, 'documentType', 'newDocumentNumber')) {
              candidates.push(`${base}.MESSAGE_DOCUMENT_NUMBER_SET`);
            } else if (ck === 'document_number_removed' && this.hasValues(p, 'documentType')) {
              candidates.push(`${base}.MESSAGE_DOCUMENT_NUMBER_REMOVED`);
            }
          } else if (ck.startsWith('due_date_')) {
            if (ck === 'due_date_changed' && this.hasValues(p, 'documentIdentity', 'formattedPreviousDueDate', 'formattedNewDueDate')) {
              candidates.push(`${base}.MESSAGE_DUE_DATE_CHANGED`);
            } else if (ck === 'due_date_set' && this.hasValues(p, 'documentIdentity', 'formattedNewDueDate')) {
              candidates.push(`${base}.MESSAGE_DUE_DATE_SET`);
            } else if (ck === 'due_date_removed' && this.hasValues(p, 'documentIdentity')) {
              candidates.push(`${base}.MESSAGE_DUE_DATE_REMOVED`);
            }
          } else if (ck.startsWith('issue_date_')) {
            if (ck === 'issue_date_changed' && this.hasValues(p, 'documentIdentity', 'formattedPreviousIssueDate', 'formattedNewIssueDate')) {
              candidates.push(`${base}.MESSAGE_ISSUE_DATE_CHANGED`);
            } else if (ck === 'issue_date_set' && this.hasValues(p, 'documentIdentity', 'formattedNewIssueDate')) {
              candidates.push(`${base}.MESSAGE_ISSUE_DATE_SET`);
            } else if (ck === 'issue_date_removed' && this.hasValues(p, 'documentIdentity')) {
              candidates.push(`${base}.MESSAGE_ISSUE_DATE_REMOVED`);
            }
          } else if (ck.startsWith('contract_start_date_')) {
            if (ck === 'contract_start_date_changed' && this.hasValues(p, 'documentIdentity', 'formattedPreviousContractStartDate', 'formattedNewContractStartDate')) {
              candidates.push(`${base}.MESSAGE_CONTRACT_START_DATE_CHANGED`);
            } else if (ck === 'contract_start_date_set' && this.hasValues(p, 'documentIdentity', 'formattedNewContractStartDate')) {
              candidates.push(`${base}.MESSAGE_CONTRACT_START_DATE_SET`);
            } else if (ck === 'contract_start_date_removed' && this.hasValues(p, 'documentIdentity')) {
              candidates.push(`${base}.MESSAGE_CONTRACT_START_DATE_REMOVED`);
            }
          } else if (ck.startsWith('document_type_') && this.hasValues(p, 'documentIdentity', 'previousDocumentType', 'newDocumentType')) {
            candidates.push(`${base}.MESSAGE_DOCUMENT_TYPE_CHANGED`);
          } else if (ck === 'financial_details_changed' && this.hasValues(p, 'documentIdentity')) {
            candidates.push(`${base}.MESSAGE_FINANCIAL_DETAILS_CHANGED`);
          } else if (ck === 'multiple') {
            if (this.hasValues(p, 'documentIdentity', 'changedFieldCount')) {
              if (this.hasValues(p, 'changedField1Label', 'changedField2Label', 'changedField3Label') && p['remainingChangedFieldCount']) {
                candidates.push(`${base}.MESSAGE_MULTIPLE_WITH_REMAINDER`);
              } else if (this.hasValues(p, 'changedField1Label', 'changedField2Label', 'changedField3Label')) {
                candidates.push(`${base}.MESSAGE_MULTIPLE_3`);
              } else if (this.hasValues(p, 'changedField1Label', 'changedField2Label')) {
                candidates.push(`${base}.MESSAGE_MULTIPLE_2`);
              } else {
                candidates.push(`${base}.MESSAGE_MULTIPLE_COUNT_ONLY`);
              }
            }
          }
        }

        if (this.hasValues(p, 'documentIdentity')) {
          candidates.push(`${base}.MESSAGE_DOCUMENT_IDENTITY_ONLY`);
        }
        if (this.hasValues(p, 'documentType', 'documentNumber')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        }
        break;
      }

      case 'billing_invoice_overdue':
        if (this.hasValues(p, 'documentType', 'documentNumber', 'formattedAmount', 'formattedDueDate')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        } else if (this.hasValues(p, 'documentType', 'documentNumber')) {
          if (this.hasValues(p, 'formattedAmount')) {
            candidates.push(`${base}.MESSAGE_DOC_AND_AMOUNT`);
          }
          if (this.hasValues(p, 'formattedDueDate')) {
            candidates.push(`${base}.MESSAGE_DOC_AND_DATE`);
          }
          candidates.push(`${base}.MESSAGE_DOC_ONLY`);
        }
        break;

      case 'billing_pending_upgrade_activated':
        if (this.hasValues(p, 'previousPlan', 'newPlan')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        }
        break;

      case 'team_seat_capacity_reached':
        if (this.hasValues(p, 'activeSeatCount', 'seatLimit')) {
          if (p['pendingInviteCount'] === '0') {
            candidates.push(`${base}.MESSAGE_NO_PENDING`);
          } else if (this.hasValues(p, 'pendingInviteCount')) {
            candidates.push(`${base}.MESSAGE_ENRICHED`);
          }
        }
        break;

      case 'property_capacity_reached':
        if (this.hasValues(p, 'usedCount', 'maximumCount')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        }
        break;

      case 'sign_order_status_changed': {
        const status = p['status'];
        if (!this.signOrderNotificationStatuses.has(status)) break;
        const statusBase = `${base}.STATUSES.${status}`;
        if (status === 'payment_received') {
          candidates.push(this.hasValues(p, 'invoiceNumber') ? `${statusBase}.BODY_WITH_INVOICE` : `${statusBase}.BODY`);
        } else if (status === 'shipped') {
          if (this.hasValues(p, 'carrier', 'trackingNumber')) candidates.push(`${statusBase}.BODY_WITH_CARRIER_AND_TRACKING`);
          else if (this.hasValues(p, 'carrier')) candidates.push(`${statusBase}.BODY_WITH_CARRIER`);
          else if (this.hasValues(p, 'trackingNumber')) candidates.push(`${statusBase}.BODY_WITH_TRACKING`);
          else candidates.push(`${statusBase}.BODY`);
        } else if (status === 'cancelled') {
          candidates.push(this.hasValues(p, 'cancellationReason') ? `${statusBase}.BODY_WITH_REASON` : `${statusBase}.BODY`);
        } else {
          candidates.push(`${statusBase}.BODY`);
        }
        break;
      }

      case 'user_welcome':
        if (this.hasValues(p, 'userIdentity') && this.hasValues(p, 'newPlan')) {
          candidates.push(`${base}.MESSAGE_ENRICHED`);
        } else if (this.hasValues(p, 'userIdentity')) {
          candidates.push(`${base}.MESSAGE_WITH_NAME`);
        } else if (this.hasValues(p, 'newPlan')) {
          candidates.push(`${base}.MESSAGE_WITH_PLAN`);
        }
        break;

      case 'user_invite_welcome':
        if (this.hasValues(p, 'companyName', 'userRole')) {
          candidates.push(`${base}.MESSAGE_SUBJECT`);
        } else if (this.hasValues(p, 'companyName')) {
          candidates.push(`${base}.MESSAGE_SUBJECT_COMPANY_ONLY`);
        } else if (this.hasValues(p, 'userRole')) {
          candidates.push(`${base}.MESSAGE_SUBJECT_ROLE_ONLY`);
        }
        break;

      case 'team_invitation_accepted':
        if (this.hasValues(p, 'subjectIdentity', 'subjectRole')) {
          candidates.push(`${base}.MESSAGE_MANAGER`);
        } else if (this.hasValues(p, 'subjectIdentity')) {
          candidates.push(`${base}.MESSAGE_MANAGER_NO_ROLE`);
        }
        break;

      case 'team_user_inactive_60d':
        if (this.hasValues(p, 'subjectIdentity', 'inactiveDays', 'subjectRole')) {
          candidates.push(`${base}.MESSAGE_MANAGER`);
        } else if (this.hasValues(p, 'subjectIdentity', 'inactiveDays')) {
          candidates.push(`${base}.MESSAGE_MANAGER_NO_ROLE`);
        } else if (this.hasValues(p, 'inactiveDays')) {
          candidates.push(`${base}.MESSAGE_GENERIC_MANAGER`);
        }
        break;

      case 'team_access_changed': {
        if (p['variant'] === 'permissions') {
          const added = parseInt(p['addedPermissionCount'], 10) || 0;
          const removed = parseInt(p['removedPermissionCount'], 10) || 0;
          const hasAddedLabel = this.hasValues(p, 'addedPermission1Label');
          const hasRemovedLabel = this.hasValues(p, 'removedPermission1Label');
          const audUpper = aud ? aud.toUpperCase() : '';

          if (added === 1 && removed === 0 && hasAddedLabel) {
            if (audUpper) candidates.push(`${base}.MESSAGE_${audUpper}_PERMISSIONS_ADDED_NAMED`);
          } else if (added === 0 && removed === 1 && hasRemovedLabel) {
            if (audUpper) candidates.push(`${base}.MESSAGE_${audUpper}_PERMISSIONS_REMOVED_NAMED`);
          }
        }
        break;
      }

      default:
        break;
    }
  }

  relativeTime(value: string): string {
    const minutes = Math.floor(Math.max(0, this.now - new Date(value).getTime()) / 60_000);
    if (minutes < 1) return this.translate.instant('NOTIFICATIONS.RELATIVE.JUST_NOW');
    if (minutes < 60) return this.translate.instant('NOTIFICATIONS.RELATIVE.MINUTES_AGO', {count: minutes});
    const hours = Math.floor(minutes / 60);
    return hours < 24 ? this.translate.instant('NOTIFICATIONS.RELATIVE.HOURS_AGO', {count: hours}) : this.translate.instant('NOTIFICATIONS.RELATIVE.DAYS_AGO', {count: Math.floor(hours / 24)});
  }

  emptyKey(): string {
    return this.readState === 'unread' ? 'NOTIFICATIONS.EMPTY.UNREAD' : this.readState === 'read' ? 'NOTIFICATIONS.EMPTY.READ' : 'NOTIFICATIONS.EMPTY.ALL';
  }

  isExpanded(item: AppNotification): boolean {
    return this.expandedNotificationIds.has(item.id);
  }

  isOverflowing(item: AppNotification): boolean {
    return this.overflowingNotificationIds.has(item.id);
  }

  toggleBodyExpansion(event: MouseEvent, item: AppNotification): void {
    this.now = Date.now();
    event.stopPropagation();
    event.preventDefault();

    if (this.expandedNotificationIds.has(item.id)) {
      this.expandedNotificationIds.delete(item.id);
      if (this.pendingCollapsedOverflowMeasurementIds.has(item.id)) {
        this.pendingCollapsedOverflowMeasurementIds.delete(item.id);
        this.scheduleOverflowMeasurement();
      }
      return;
    }

    this.expandedNotificationIds.add(item.id);
    if (!item.readAt) {
      this.notifications.markRead(item.id).subscribe({
        next: () => {
          item.readAt = new Date().toISOString();
          this.notifications.refreshUnreadCount();
        }
      });
    }
  }

  private detectBodyOverflow(): void {
    if (!this.bodyElements) return;
    for (const el of this.bodyElements) {
      const id = el.nativeElement.getAttribute('data-notification-id');
      if (!id) continue;
      if (this.expandedNotificationIds.has(id)) continue;
      this.pendingCollapsedOverflowMeasurementIds.delete(id);
      const overflowed = el.nativeElement.scrollHeight > el.nativeElement.clientHeight;
      if (overflowed) {
        this.overflowingNotificationIds.add(id);
      } else {
        this.overflowingNotificationIds.delete(id);
      }
    }
  }

  private scheduleOverflowMeasurement(): void {
    if (!this.visible) return;
    if (this.overflowMeasurementFrame !== null) return;
    this.overflowMeasurementFrame = requestAnimationFrame(() => {
      this.overflowMeasurementFrame = null;
      if (!this.visible) return;
      if (!this.bodyElements?.length) return;
      this.detectBodyOverflow();
    });
  }

  private cancelPendingOverflowMeasurement(): void {
    if (this.overflowMeasurementFrame !== null) {
      cancelAnimationFrame(this.overflowMeasurementFrame);
      this.overflowMeasurementFrame = null;
    }
  }

  private cleanupStaleIds(): void {
    const currentIds = new Set(this.items.map((i) => i.id));
    for (const id of this.expandedNotificationIds) {
      if (!currentIds.has(id)) this.expandedNotificationIds.delete(id);
    }
    for (const id of this.overflowingNotificationIds) {
      if (!currentIds.has(id)) this.overflowingNotificationIds.delete(id);
    }
    for (const id of this.pendingCollapsedOverflowMeasurementIds) {
      if (!currentIds.has(id)) this.pendingCollapsedOverflowMeasurementIds.delete(id);
    }
  }

  private attachPanelResizeObserver(): void {
    this.disconnectPanelResizeObserver();
    if (!this.visible) return;
    const el = this.panelElementRef?.nativeElement;
    if (!el) return;
    this.observedPanelWidth = null;
    this.panelResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = Math.round(entry.contentRect.width);
        if (this.observedPanelWidth === null && width > 0) {
          this.observedPanelWidth = width;
          this.scheduleOverflowMeasurement();
          return;
        }
        if (width !== this.observedPanelWidth) {
          this.observedPanelWidth = width;
          for (const id of this.expandedNotificationIds) {
            this.pendingCollapsedOverflowMeasurementIds.add(id);
          }
          this.scheduleOverflowMeasurement();
          return;
        }
      }
    });
    this.panelResizeObserver.observe(el);
  }

  private disconnectPanelResizeObserver(): void {
    if (this.panelResizeObserver) {
      this.panelResizeObserver.disconnect();
      this.panelResizeObserver = undefined;
    }
  }

  private showTargetUnavailable(): void {
    this.message.info(this.translate.instant('NOTIFICATIONS.TARGET_UNAVAILABLE'));
    this.close();
  }

  private showTargetAccessDenied(): void {
    this.message.info(this.translate.instant('NOTIFICATIONS.TARGET_ACCESS_DENIED'));
    this.close();
  }

  private showPropertiesAccessDenied(): void {
    this.message.info(this.translate.instant('NOTIFICATIONS.PROPERTIES_ACCESS_DENIED'));
    this.close();
  }

  private showCurrentTarget(): void {
    this.message.info(this.translate.instant('NOTIFICATIONS.TARGET_CURRENT_PAGE'));
    this.close();
  }

  private isTeamAccessUnavailable(item: AppNotification): boolean {
    return item.target?.kind === 'team_access' && !this.auth.hasAnyPermissions(this.teamAccessPermissions);
  }

  private isPropertiesUnavailable(item: AppNotification): boolean {
    return (item.target?.kind === 'property_import' || item.target?.kind === 'properties')
      && !this.auth.hasPermission(this.propertyReadPermission);
  }

  private isCurrentTarget(target: { commands: string[]; queryParams?: Record<string, string> }): boolean {
    const currentPath = this.router.url.split(/[?#]/, 1)[0];
    const targetUrl = this.router.serializeUrl(this.router.createUrlTree(target.commands, target.queryParams ? { queryParams: target.queryParams } : {}));
    return this.router.url === targetUrl || currentPath === targetUrl.split(/[?#]/, 1)[0] && !target.queryParams;
  }

  private targetNavigation(item: AppNotification): { commands: string[]; queryParams?: Record<string, string> } | null {
    switch (item.target?.kind) {
      case 'dashboard':
        return { commands: ['/dashboard'] };
      case 'company':
        return { commands: ['/company'] };
      case 'qr_template':
        return { commands: ['/qr-poster-template'] };
      case 'contacts':
        return { commands: ['/contacts'] };
      case 'announcements':
        return { commands: ['/announcements'] };
      case 'audit_logs':
        return { commands: ['/audit-logs'] };
      case 'account_settings':
        return { commands: ['/account/settings/account'] };
      case 'property_import':
        return { commands: ['/properties'] };
      case 'integration_sync':
        return { commands: item.target.id ? ['/company/integrations', item.target.id, 'status'] : ['/company/integrations'] };
      case 'integrations':
        return { commands: ['/company/integrations'] };
      case 'billing':
        return { commands: ['/account/settings/billing'] };
      case 'team_access':
        return { commands: ['/account/settings/team-access'] };
      case 'properties':
        return { commands: ['/properties'] };
      case 'sign_order_detail':
        return item.target.id ? {
          commands: ['/qr-poster-template'],
          queryParams: { signOrderPublicId: item.target.id },
        } : null;
      default:
        return null;
    }
  }
}
